import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useSalePrefix } from '../lib/listingPaths';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useApi } from '../lib/useApi';
import { useMutation, compact, numOrUndef } from '../lib/useMutation';
import { apiRequest, ApiError } from '../lib/api';
import { downscaleWithThumb, fileSize } from '../lib/downscale';
import { AuthedImage } from '../components/AuthedImage';
import {
  ListingTourEditor, SALE_TOUR, type TourCoverage, type ListingVideo,
} from '../components/ListingTourEditor';
import { COUNTIES_BY_USE } from '../lib/counties';
import { shortDate } from '../lib/format';
import {
  Alert, Button, Card, Field, LoadingBlock, Select, StatusPill, TextInput,
} from '../components/ui';

/**
 * Creating and managing one property for sale. One component for both, as
 * ListingForm is, because "add" and "manage" are the same nine fields and a
 * second component would be the same form with a different bug in it.
 *
 * The banner at the top is the part that earns its place. A sale listing is
 * held up by one of two independent things, and a seller who is told only
 * "approved" while nothing appears on the site will conclude the site is
 * broken. So the state is spelled out with the next action in it.
 */

interface Gates {
  reviewed: boolean; paid: boolean; paidUntil: string | null;
  publishable: boolean; blockedBy: 'review' | 'payment' | null;
}

interface Listing {
  id: string; title: string; description: string | null; property_type: string | null;
  bedrooms: number | null; bathrooms: number | null; county: string | null;
  estate_area: string | null; physical_address: string | null;
  asking_price: string; price_negotiable: boolean;
  land_size: string | null; land_size_unit: string | null; floor_area_sqm: string | null;
  tenure: string; lease_years_remaining: number | null; title_deed_status: string;
  year_built: number | null; service_charge_monthly: string | null;
  mortgage_accepted: boolean | null; amenities: string[];
  contact_preference: string; contact_name: string | null;
  contact_phone: string | null; contact_email: string | null;
  status: string; rejection_reason: string | null; paid_until: string | null;
}

interface SalePhoto { id: string; position: number; mime_type: string; size_bytes: number }

interface Options {
  amenities: { code: string; label: string }[];
  propertyTypes: { code: string; label: string }[];
  maxPhotos: number;
}

const EMPTY = {
  title: '', description: '', propertyType: '', bedrooms: '', bathrooms: '',
  county: '', estateArea: '', physicalAddress: '',
  askingPrice: '', priceNegotiable: false,
  landSize: '', landSizeUnit: '', floorAreaSqm: '',
  tenure: 'unknown', leaseYearsRemaining: '', titleDeedStatus: 'unknown',
  yearBuilt: '', serviceChargeMonthly: '', mortgageAccepted: '',
  amenities: [] as string[],
  contactPreference: 'none', contactName: '', contactPhone: '', contactEmail: '',
};

/** The seller may edit only these; anything further along is frozen. */
const EDITABLE = ['draft', 'rejected'];
/** Photographs, the 360 tour and the video stay editable while the property is listed. */
const MEDIA_EDITABLE = ['draft', 'rejected', 'pending_review', 'approved', 'published', 'reserved'];

const TENURES: [string, string][] = [
  ['unknown', 'Not stated'], ['freehold', 'Freehold'], ['leasehold', 'Leasehold'],
];
const TITLES: [string, string][] = [
  ['unknown', 'Not stated'], ['ready', 'Title ready'], ['in_process', 'Title in process'],
  ['sectional', 'Sectional title'], ['share_certificate', 'Share certificate'], ['none', 'No title'],
];
const UNITS: [string, string][] = [
  ['', 'Unit'], ['acres', 'Acres'], ['hectares', 'Hectares'], ['sqm', 'Square metres'], ['sqft', 'Square feet'],
];

export function SaleListingForm() {
  const salePrefix = useSalePrefix();
  const navigate = useNavigate();
  const { id } = useParams();
  const [sp] = useSearchParams();
  const isNew = !id;

  const options = useApi<Options>('/api/v1/sale-listings/options');
  const existing = useApi<{
    listing: Listing; photos: SalePhoto[]; gates: Gates;
    tour: TourCoverage; video: ListingVideo | null;
  }>(
    id ? `/api/v1/sale-listings/${id}` : null);
  // Brief §7 — converting a managed property. Only asked for when creating,
  // and only when a property was named.
  const defaults = useApi<{ defaults: Record<string, unknown> }>(
    isNew && sp.get('propertyId')
      ? `/api/v1/sale-listings/defaults/${sp.get('propertyId')}`
      : null,
    sp.get('unitId') ? { unitId: sp.get('unitId')! } : undefined);

  const { run, busy, error } = useMutation<{ listing: { id: string } }>();
  const [f, setF] = useState(EMPTY);
  const [note, setNote] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((s) => ({ ...s, [k]: e.target.value }));

  useEffect(() => {
    const l = existing.data?.listing;
    if (!l) return;
    setF({
      title: l.title, description: l.description ?? '', propertyType: l.property_type ?? '',
      bedrooms: l.bedrooms?.toString() ?? '', bathrooms: l.bathrooms?.toString() ?? '',
      county: l.county ?? '', estateArea: l.estate_area ?? '',
      physicalAddress: l.physical_address ?? '',
      askingPrice: l.asking_price, priceNegotiable: l.price_negotiable,
      landSize: l.land_size ?? '', landSizeUnit: l.land_size_unit ?? '',
      floorAreaSqm: l.floor_area_sqm ?? '',
      tenure: l.tenure, leaseYearsRemaining: l.lease_years_remaining?.toString() ?? '',
      titleDeedStatus: l.title_deed_status, yearBuilt: l.year_built?.toString() ?? '',
      serviceChargeMonthly: l.service_charge_monthly ?? '',
      mortgageAccepted: l.mortgage_accepted === null ? '' : String(l.mortgage_accepted),
      amenities: l.amenities ?? [],
      contactPreference: l.contact_preference, contactName: l.contact_name ?? '',
      contactPhone: l.contact_phone ?? '', contactEmail: l.contact_email ?? '',
    });
  }, [existing.data]);

  useEffect(() => {
    const d = defaults.data?.defaults;
    if (!d) return;
    setF((s) => ({
      ...s,
      title: (d.title as string) ?? s.title,
      propertyType: (d.propertyType as string) ?? s.propertyType,
      bedrooms: d.bedrooms?.toString() ?? s.bedrooms,
      bathrooms: d.bathrooms?.toString() ?? s.bathrooms,
      county: (d.county as string) ?? s.county,
      estateArea: (d.estateArea as string) ?? s.estateArea,
      physicalAddress: (d.physicalAddress as string) ?? s.physicalAddress,
      yearBuilt: d.yearBuilt?.toString() ?? s.yearBuilt,
    }));
  }, [defaults.data]);

  const listing = existing.data?.listing;
  const gates = existing.data?.gates;
  const editable = isNew || (listing ? EDITABLE.includes(listing.status) : false);
  const mediaEditable = !isNew && !!listing && MEDIA_EDITABLE.includes(listing.status);
  const photos = existing.data?.photos ?? [];
  const maxPhotos = options.data?.maxPhotos ?? 12;

  /*
   * Shrunk on this device before being sent, exactly as a rental photo is: a
   * phone photograph is 4-5MB and what leaves here is nearer 200KB. That
   * matters twice over for a sale listing — the seller is on a mobile bundle
   * while uploading twelve of them, and a buyer is on one while loading the
   * gallery.
   */
  const onFiles = async (files: FileList | null) => {
    if (!files?.length || !id) return;
    const room = maxPhotos - photos.length;
    if (room <= 0) { setPhotoError(`A listing may have at most ${maxPhotos} photographs.`); return; }

    setUploading(true); setPhotoError(null); setNote(null);
    try {
      let added = 0;
      for (const file of Array.from(files).slice(0, room)) {
        // The 320px thumbnail comes from the same decode (145): it is what
        // the cards and the phone's welcome strip download instead.
        const small = await downscaleWithThumb(file);
        await apiRequest(`/api/v1/sale-listings/${id}/photos`, {
          method: 'POST',
          body: { mimeType: small.mimeType, contentBase64: small.contentBase64, thumbBase64: small.thumbBase64 },
        });
        added++;
        setNote(`${file.name} — ${fileSize(file.size)} shrunk to ${fileSize(small.bytes)}`);
      }
      if (files.length > room) {
        setPhotoError(`Only ${added} added — a listing may have at most ${maxPhotos} photographs.`);
      }
      existing.reload();
    } catch (e) {
      setPhotoError(e instanceof ApiError ? e.message : 'That photograph could not be added.');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const removePhoto = async (photoId: string) => {
    setPhotoError(null);
    try {
      await apiRequest(`/api/v1/sale-listings/${id}/photos/${photoId}`, { method: 'DELETE' });
      existing.reload();
    } catch (e) {
      setPhotoError(e instanceof ApiError ? e.message : 'That photograph could not be removed.');
    }
  };

  const body = () => compact({
    propertyId: sp.get('propertyId') ?? undefined,
    unitId: sp.get('unitId') ?? undefined,
    title: f.title.trim(),
    description: f.description.trim(),
    propertyType: f.propertyType,
    bedrooms: numOrUndef(f.bedrooms),
    bathrooms: numOrUndef(f.bathrooms),
    county: f.county,
    estateArea: f.estateArea.trim(),
    physicalAddress: f.physicalAddress.trim(),
    askingPrice: f.askingPrice,
    priceNegotiable: f.priceNegotiable,
    landSize: numOrUndef(f.landSize),
    landSizeUnit: f.landSizeUnit || undefined,
    floorAreaSqm: numOrUndef(f.floorAreaSqm),
    tenure: f.tenure,
    leaseYearsRemaining: f.tenure === 'leasehold' ? numOrUndef(f.leaseYearsRemaining) : undefined,
    titleDeedStatus: f.titleDeedStatus,
    yearBuilt: numOrUndef(f.yearBuilt),
    serviceChargeMonthly: numOrUndef(f.serviceChargeMonthly),
    mortgageAccepted: f.mortgageAccepted === '' ? undefined : f.mortgageAccepted === 'true',
    amenities: f.amenities,
    contactPreference: f.contactPreference,
    contactName: f.contactName.trim(),
    contactPhone: f.contactPhone.trim(),
    contactEmail: f.contactEmail.trim(),
  });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (isNew) {
        const r = await run('/api/v1/sale-listings', { body: body() });
        navigate(`${salePrefix}/${r.listing.id}`, { replace: true });
      } else {
        await run(`/api/v1/sale-listings/${id}`, { method: 'PATCH', body: body() });
        setNote('Saved.');
        existing.reload();
      }
    } catch { /* surfaced by the hook */ }
  };

  const submit = async () => {
    try {
      if (!isNew) await run(`/api/v1/sale-listings/${id}`, { method: 'PATCH', body: body() });
      await run(`/api/v1/sale-listings/${id}/submit`, { body: {} });
      navigate(salePrefix, { replace: true });
    } catch { /* surfaced */ }
  };

  const withdraw = async () => {
    try {
      await run(`/api/v1/sale-listings/${id}/withdraw`, { body: {} });
      setNote('Withdrawn. It is a draft again and no longer public.');
      existing.reload();
    } catch { /* surfaced */ }
  };

  const toggleAmenity = (code: string) => setF((s) => ({
    ...s,
    amenities: s.amenities.includes(code)
      ? s.amenities.filter((a) => a !== code)
      : [...s.amenities, code],
  }));

  if ((id && existing.loading) || options.loading) {
    return <div className="page"><LoadingBlock /></div>;
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{isNew ? 'List a property for sale' : 'Manage listing'}</h1>
          <p className="page-sub">
            {isNew
              ? 'Preparing it is free. A person reviews it, then you pay to publish it.'
              : 'Changes apply to the advert buyers see.'}
          </p>
        </div>
      </div>

      {error && <div style={{ marginBottom: 16 }}><Alert tone="danger">{error.message}</Alert></div>}
      {note && <div style={{ marginBottom: 16 }}><Alert tone="success">{note}</Alert></div>}

      {/* The two gates, in words the seller can act on. */}
      {listing && gates && (
        <div style={{ marginBottom: 16 }}>
          {listing.status === 'rejected' && listing.rejection_reason ? (
            <Alert tone="warning">
              <strong>Refused:</strong> {listing.rejection_reason} Make the change and submit it again.
            </Alert>
          ) : listing.status === 'pending_review' ? (
            <Alert tone="info">
              With our team for review, usually within a day. Withdraw it to make changes.
            </Alert>
          ) : listing.status === 'approved' && !gates.paid ? (
            <Alert tone="warning">
              <strong>Approved.</strong> It goes live once it holds a publication slot.{' '}
              <Link to={`${salePrefix}/${listing.id}/pay`}>Publish it</Link>
            </Alert>
          ) : listing.status === 'published' && gates.paid ? (
            <Alert tone="success">
              Live on Buy Property until {shortDate(gates.paidUntil!)}.{' '}
              <Link to={`${salePrefix}/${listing.id}/pay`}>Extend</Link>
            </Alert>
          ) : (listing.status === 'expired' || (listing.status === 'published' && !gates.paid)) ? (
            <Alert tone="warning">
              <strong>Off the site.</strong> The publication slot has run out.{' '}
              <Link to={`${salePrefix}/${listing.id}/pay`}>Renew it</Link>
            </Alert>
          ) : !editable ? (
            <Alert tone="info">
              <StatusPill status={listing.status} /> Withdraw it to change the details. Photographs,
              the 360 tour and the video can be changed while it stays listed.
            </Alert>
          ) : null}
        </div>
      )}

      <form onSubmit={save}>
        <div className="stack">
          <Card title="The advert">
            <Field label="Title" htmlFor="s-title" hint="What a buyer sees first.">
              <TextInput id="s-title" value={f.title} onChange={set('title')}
                disabled={!editable} required maxLength={140} />
            </Field>
            <Field label="Description" htmlFor="s-desc">
              <textarea id="s-desc" className="input" rows={5} value={f.description}
                onChange={set('description')} disabled={!editable} maxLength={6000} />
            </Field>
          </Card>

          <Card title="The property">
            <div className="grid grid-2">
              <Field label="Type" htmlFor="s-type">
                <Select id="s-type" value={f.propertyType} onChange={set('propertyType')} disabled={!editable}>
                  <option value="">Choose…</option>
                  {options.data!.propertyTypes.map((t) => (
                    <option key={t.code} value={t.code}>{t.label}</option>
                  ))}
                </Select>
              </Field>
              <Field label="County" htmlFor="s-county"
                hint="A free-text county is a listing nobody can find.">
                <Select id="s-county" value={f.county} onChange={set('county')} disabled={!editable}>
                  <option value="">Choose…</option>
                  {COUNTIES_BY_USE.map((c) => <option key={c} value={c}>{c}</option>)}
                </Select>
              </Field>
              <Field label="Bedrooms" htmlFor="s-beds">
                <TextInput id="s-beds" type="number" min={0} value={f.bedrooms}
                  onChange={set('bedrooms')} disabled={!editable} />
              </Field>
              <Field label="Bathrooms" htmlFor="s-baths">
                <TextInput id="s-baths" type="number" min={0} value={f.bathrooms}
                  onChange={set('bathrooms')} disabled={!editable} />
              </Field>
              <Field label="Estate or area" htmlFor="s-area">
                <TextInput id="s-area" value={f.estateArea} onChange={set('estateArea')} disabled={!editable} />
              </Field>
              <Field label="Physical address" htmlFor="s-addr" hint="Optional. Shown to buyers.">
                <TextInput id="s-addr" value={f.physicalAddress} onChange={set('physicalAddress')} disabled={!editable} />
              </Field>
            </div>
          </Card>

          <Card title="Price and land">
            <div className="grid grid-2">
              <Field label="Asking price (KES)" htmlFor="s-price">
                <TextInput id="s-price" type="number" min={0} value={f.askingPrice}
                  onChange={set('askingPrice')} disabled={!editable} required />
              </Field>
              <Field label="Negotiable?" htmlFor="s-neg"
                hint="Saying so brings more calls than pricing defensively high.">
                <Select id="s-neg" value={String(f.priceNegotiable)} disabled={!editable}
                  onChange={(e) => setF((s) => ({ ...s, priceNegotiable: e.target.value === 'true' }))}>
                  <option value="false">Fixed price</option>
                  <option value="true">Negotiable</option>
                </Select>
              </Field>
              <Field label="Land size" htmlFor="s-land"
                hint="Give the size and the unit together, or leave both blank.">
                <TextInput id="s-land" type="number" step="0.001" min={0} value={f.landSize}
                  onChange={set('landSize')} disabled={!editable} />
              </Field>
              <Field label="Unit" htmlFor="s-landunit">
                <Select id="s-landunit" value={f.landSizeUnit} onChange={set('landSizeUnit')} disabled={!editable}>
                  {UNITS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </Select>
              </Field>
              <Field label="Floor area (sqm)" htmlFor="s-floor">
                <TextInput id="s-floor" type="number" step="0.01" min={0} value={f.floorAreaSqm}
                  onChange={set('floorAreaSqm')} disabled={!editable} />
              </Field>
              <Field label="Service charge (KES/month)" htmlFor="s-svc">
                <TextInput id="s-svc" type="number" min={0} value={f.serviceChargeMonthly}
                  onChange={set('serviceChargeMonthly')} disabled={!editable} />
              </Field>
            </div>
          </Card>

          <Card title="Title and tenure">
            <div className="grid grid-2">
              <Field label="Tenure" htmlFor="s-tenure">
                <Select id="s-tenure" value={f.tenure} onChange={set('tenure')} disabled={!editable}>
                  {TENURES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </Select>
              </Field>
              {f.tenure === 'leasehold' && (
                <Field label="Years remaining on the lease" htmlFor="s-lease">
                  <TextInput id="s-lease" type="number" min={0} max={999} value={f.leaseYearsRemaining}
                    onChange={set('leaseYearsRemaining')} disabled={!editable} />
                </Field>
              )}
              <Field label="Title deed" htmlFor="s-title-status">
                <Select id="s-title-status" value={f.titleDeedStatus} onChange={set('titleDeedStatus')} disabled={!editable}>
                  {TITLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </Select>
              </Field>
              <Field label="Year built" htmlFor="s-year">
                <TextInput id="s-year" type="number" min={1800} max={2200} value={f.yearBuilt}
                  onChange={set('yearBuilt')} disabled={!editable} />
              </Field>
              <Field label="Mortgage buyers" htmlFor="s-mortgage"
                hint="Buyers ask this early. Answering it saves a wasted viewing.">
                <Select id="s-mortgage" value={f.mortgageAccepted} onChange={set('mortgageAccepted')} disabled={!editable}>
                  <option value="">Not stated</option>
                  <option value="true">Accepted</option>
                  <option value="false">Cash only</option>
                </Select>
              </Field>
            </div>
          </Card>

          <Card title="Features">
            <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
              {options.data!.amenities.map((a) => (
                <button key={a.code} type="button" disabled={!editable}
                  className={`lf-chip${f.amenities.includes(a.code) ? ' on' : ''}`}
                  aria-pressed={f.amenities.includes(a.code)}
                  onClick={() => toggleAmenity(a.code)}>
                  {a.label}
                </button>
              ))}
            </div>
          </Card>

          <Card title="How buyers reach you">
            <Field label="Show buyers" htmlFor="s-pref"
              hint="Only what you choose here appears on the public page. Enquiries reach you either way.">
              <Select id="s-pref" value={f.contactPreference} onChange={set('contactPreference')} disabled={!editable}>
                <option value="none">Nothing — enquiry form only</option>
                <option value="phone">My phone number</option>
                <option value="email">My email address</option>
                <option value="both">Both</option>
              </Select>
            </Field>
            <div className="grid grid-2">
              <Field label="Contact name" htmlFor="s-cname">
                <TextInput id="s-cname" value={f.contactName} onChange={set('contactName')} disabled={!editable} />
              </Field>
              <Field label="Phone" htmlFor="s-cphone">
                <TextInput id="s-cphone" value={f.contactPhone} onChange={set('contactPhone')} disabled={!editable} />
              </Field>
              <Field label="Email" htmlFor="s-cemail">
                <TextInput id="s-cemail" type="email" value={f.contactEmail} onChange={set('contactEmail')} disabled={!editable} />
              </Field>
            </div>
          </Card>

          {isNew ? (
            <Card title="Photographs">
              <p className="page-sub" style={{ marginTop: 0 }}>
                Save the draft first, then add photographs.
              </p>
            </Card>
          ) : (
            <Card title="Photographs">
              <p className="page-sub" style={{ marginTop: 0 }}>
                Up to {maxPhotos}. A listing needs at least one before it can be submitted —
                a buyer will not open one without a picture.
              </p>
              {photoError && <Alert tone="danger">{photoError}</Alert>}

              <div className="lf-photos">
                {photos.map((ph) => (
                  <div className="lf-photo" key={ph.id}>
                    {/* Not a bare <img>: this route needs a Bearer token, which
                        an img tag cannot send — see AuthedImage. */}
                    <AuthedImage src={`/api/v1/sale-listings/photos/${ph.id}/content`} alt="" />
                    {mediaEditable && (
                      <button type="button" className="lf-photo-x" aria-label="Remove photograph"
                        onClick={() => removePhoto(ph.id)}>×</button>
                    )}
                    <span className="lf-photo-size">{fileSize(ph.size_bytes)}</span>
                  </div>
                ))}
                {mediaEditable && photos.length < maxPhotos && (
                  <button type="button" className="lf-photo-add"
                    onClick={() => fileInput.current?.click()} disabled={uploading}>
                    <span aria-hidden>+</span>
                    <span>{uploading ? 'Adding…' : 'Add photograph'}</span>
                  </button>
                )}
              </div>
              <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp"
                multiple hidden onChange={(e) => onFiles(e.target.files)} />
              <p className="page-sub" style={{ marginTop: 8 }}>
                They are resized on your device before uploading, so a large photograph
                from your phone is fine.
              </p>
            </Card>
          )}
          {!isNew && id && existing.data?.tour && (
            <ListingTourEditor
              listingId={id}
              editable={mediaEditable}
              tour={existing.data.tour}
              video={existing.data.video}
              maxVideoBytes={80 * 1024 * 1024}
              onChange={() => existing.reload()}
              urls={SALE_TOUR}
            />
          )}
        </div>

        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 16, gap: 12 }}>
          <Link className="btn btn-secondary" to={`${salePrefix}`}>Cancel</Link>
          {editable ? (
            <>
              <Button type="submit" variant="secondary" loading={busy}>
                {isNew ? 'Save draft' : 'Save'}
              </Button>
              {!isNew && (
                <Button type="button" loading={busy} onClick={submit}>
                  Submit for review
                </Button>
              )}
            </>
          ) : (
            <Button type="button" variant="secondary" loading={busy} onClick={withdraw}>
              Withdraw
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
